import classnames from 'classnames';
import Image from "@/app/[lang]/components/shared/SmartImage";
import { FiX } from 'react-icons/fi';

import style from '../style.module.scss';
import StationMetaRows from './StationMetaRows';
import StationPricingCards from './StationPricingCards';

interface StationCardProps {
    station: any;
    stationIconSrc: string;
    isActive: boolean;
    googleMapsUrl: string | null;
    statusLabel: string;
    statusClassName: string;
    distanceLabel: string;
    distanceReferenceLabel: string;
    onSelect: () => void;
    onDeselect: () => void;
    onClose: () => void;
    getConnectorIcon: (connectorType: string) => { src: string } | null;
    cardRef?: (element: HTMLDivElement | null) => void;
}

const StationCard = ({
    station,
    stationIconSrc,
    isActive,
    googleMapsUrl,
    statusLabel,
    statusClassName,
    distanceLabel,
    distanceReferenceLabel,
    onSelect,
    onDeselect,
    onClose,
    getConnectorIcon,
    cardRef,
}: StationCardProps) => {
    const data = station.properties?.data;

    if (!data) return null;

    const stationId = station.properties?.id ?? data.id;

    return (
        <div
            ref={cardRef}
            role="button"
            tabIndex={0}
            className={classnames(style.stationCard, isActive && style.stationCardActive)}
            onClick={() => {
                if (isActive) {
                    onDeselect();
                    return;
                }

                onSelect();
            }}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();

                    if (isActive) {
                        onDeselect();
                        return;
                    }

                    onSelect();
                }
            }}
        >
            <div className={style.stationHeader}>
                <span className={style.stationIcon}>
                    <Image src={stationIconSrc} alt="Station icon" width={28} height={28} />
                </span>
                <div className={style.stationTitle}>{data.name ?? `Stație ${stationId}`}</div>
                {isActive && (
                    <button
                        type="button"
                        className={style.stationCloseIcon}
                        aria-label="Închide detaliile stației"
                        onClick={(event) => {
                            event.stopPropagation();
                            onClose();
                        }}
                    >
                        <FiX />
                    </button>
                )}
            </div>
            {data.location && <div className={style.stationAddress}>{data.location}</div>}

            <StationMetaRows
                openingHours={data.openingHours}
                stationType={data.type}
                statusLabel={statusLabel}
                statusClassName={statusClassName}
                distanceReferenceLabel={distanceReferenceLabel}
                distanceLabel={distanceLabel}
            />

            {isActive && (
                <div className={classnames(style.stationExpanded, style.stationExpandedInline)}>
                    {Array.isArray(data.pricing) && (
                        <StationPricingCards
                            pricing={data.pricing}
                            stationId={stationId}
                            pricingKeyPrefix="pricing"
                            promoLabel="1.50 RON/kWh"
                            getConnectorIcon={getConnectorIcon}
                        />
                    )}

                    <a
                        href={googleMapsUrl ?? '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={style.stationActionButton}
                        onClick={(event) => {
                            if (!googleMapsUrl) {
                                event.preventDefault();
                            }
                            event.stopPropagation();
                        }}
                    >
                        Mergi la stația de încărcare
                    </a>
                </div>
            )}
        </div>
    );
};

export default StationCard;
